import { Tooltip } from "antd";
import React from "react";
import { ZAttrMeta } from "src/business/attribute";
import { filterViewableAttrs } from "src/business/attribute/filterViewableAttrs";
import { ZCatalogClassifierItem } from "src/business/catalog/ZCatalog";
import { ZCatalogRecordSafe } from "src/business/records/ZCatalogRecordSafe";
import { ZSpecificationTyped } from "src/business/specification";
import {
  columnDisplayId,
  sysColumnsHistory,
} from "src/business/sysData/sysColumns";
import { RtColumn } from "src/components/ReactiveTable/RtColumn";
import { ZSidLabel } from "src/types";

import { makeAttrColumn } from "../attribute/makeAttrColumn";
import { makeHierarchicalLabel } from "../sysData/SysParentsColumn";

type AttrSpec = ZAttrMeta & {
  specName: string;
};

const makeColumn = (attr: AttrSpec): RtColumn<ZCatalogRecordSafe> =>
  makeAttrColumn(attr, attr.specName);

export const createRecordColumns = (
  schemes: ZSpecificationTyped[],
  displayAttribute: ZSidLabel | null | undefined,
  classifiers: ZCatalogClassifierItem[] | null | undefined,
): RtColumn<ZCatalogRecordSafe>[] => {
  const srcAttrs: AttrSpec[] = schemes.flatMap((scheme) =>
    filterViewableAttrs(scheme.definition?.attributes ?? []).map((attr) => ({
      ...attr,
      specName: scheme.name,
    })),
  );
  const displayUid = displayAttribute?.id;
  const nameAttr = displayUid
    ? srcAttrs.find(({ uid }) => uid === displayUid)
    : undefined;
  let dstAttrs = srcAttrs;
  if (nameAttr) {
    dstAttrs = [nameAttr, ...srcAttrs.filter(({ uid }) => uid !== displayUid)];
  }

  const safeClassifiers = classifiers ?? [];

  const classifierColumns: RtColumn<ZCatalogRecordSafe>[] = safeClassifiers.map(
    (classifier) => ({
      key: classifier.id,
      title: classifier.name,
      render: (row) => {
        const classification = row.classification.find(
          (cls) => cls.classifierId === classifier.id,
        );
        const allParentLabels = classification?.classes.map((cls) => ({
          classId: cls.id,
          label: makeHierarchicalLabel(cls.parents, cls.label),
        }));
        const firstItem = allParentLabels?.[0];
        const firstLabel = firstItem?.label;
        const extraLabels = allParentLabels ?? [];
        const count = extraLabels.length;
        if (count > 1) {
          return (
            <Tooltip
              title={extraLabels.map((item) => (
                <div key={item.classId}>{item.label}</div>
              ))}
            >
              {firstLabel}
              {` + (${count - 1})`}
            </Tooltip>
          );
        }
        return firstLabel;
      },
    }),
  );
  return [
    columnDisplayId,
    ...dstAttrs.map(makeColumn),
    ...classifierColumns,
    ...sysColumnsHistory,
  ];
};
